/** Adapter between the API task (raw Prisma + optional client/assignee relations) and the UI `Task`. */

import type { ChecklistItem, Priority, Task, TaskStatus, TeamRole } from "@/types";

export interface ApiTask {
  id: string;
  title: string;
  description?: string | null;
  clientId?: string | null;
  client?: { id: string; name: string } | null;
  projectId?: string | null;
  assigneeId?: string | null;
  assignee?: {
    id: string;
    name: string;
    email: string;
    isActive: boolean;
    teamMember?: { id: string; name: string; email: string; role: TeamRole; status: string } | null;
  } | null;
  priority: Priority;
  status: TaskStatus;
  dueDate?: string | null;
  progress: number | string;
  // spec §4
  startAt?: string | null;
  endAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  completionNote?: string | null;
  validatorId?: string | null;
  validator?: { id: string; name: string } | null;
  tags?: string[] | null;
  links?: string[] | null;
  checklist?: unknown;
  rating?: number | null;
  files?: Array<{ id: string; name: string; mimeType?: string | null; sizeBytes?: number | null }>;
}

export interface TaskInput {
  title: string;
  description?: string;
  clientId?: string;
  clientName?: string;
  projectId?: string;
  teamMemberId?: string;
  assigneeId?: string;
  assignee?: string;
  priority: Priority;
  status: TaskStatus;
  progress?: number;
  /** Spec §2 — planification optionnelle: `""` efface la date côté API. */
  startAt?: string;
  endAt?: string;
  validatorId?: string;
  tags?: string[];
  links?: string[];
  checklist?: ChecklistItem[];
  files?: Array<{ name: string; mimeType?: string; dataBase64: string }>;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

/** ISO datetime → "YYYY-MM-DD". */
function formatDate(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return undefined;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Normalize the checklist JSON: the tasks form stores a plain array of
 * {label, done}; the CRM activation stores {items: [...], projectStage}.
 */
export function normalizeChecklist(raw: unknown): ChecklistItem[] {
  const source = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { items?: unknown }).items)
      ? (raw as { items: unknown[] }).items
      : [];
  return source
    .filter(
      (item): item is { label?: unknown; done?: unknown } => !!item && typeof item === "object",
    )
    .map((item) => ({
      label: typeof item.label === "string" ? item.label : "",
      done: item.done === true,
    }))
    .filter((item) => item.label.length > 0);
}

export function toTask(api: ApiTask, clientNameById: Map<string, string>): Task {
  const teamMember = api.assignee?.teamMember;
  return {
    id: api.id,
    title: api.title,
    description: api.description ?? undefined,
    clientId: api.clientId ?? undefined,
    clientName:
      api.client?.name ??
      (api.clientId ? (clientNameById.get(api.clientId) ?? undefined) : undefined),
    projectId: api.projectId ?? undefined,
    assigneeId: api.assigneeId ?? undefined,
    assigneeTeamMemberId: teamMember?.id,
    assignee: teamMember?.name ?? api.assignee?.name ?? undefined,
    assigneeRole: teamMember?.role,
    priority: api.priority,
    status: api.status,
    dueDate: formatDate(api.dueDate),
    progress: toNumber(api.progress),
    startAt: api.startAt ?? undefined,
    endAt: api.endAt ?? undefined,
    startedAt: api.startedAt ?? undefined,
    completedAt: api.completedAt ?? undefined,
    completionNote: api.completionNote ?? undefined,
    validatorId: api.validatorId ?? undefined,
    validatorName: api.validator?.name,
    tags: api.tags ?? [],
    links: api.links ?? [],
    checklist: normalizeChecklist(api.checklist),
    rating: api.rating ?? undefined,
    files: (api.files ?? []).map((f) => ({
      id: f.id,
      name: f.name,
      mimeType: f.mimeType ?? undefined,
      sizeBytes: f.sizeBytes ?? undefined,
    })),
  };
}

/**
 * API create/update body. UI assignment uses TeamMember IDs; the API resolves them to User IDs.
 * `startAt`/`endAt`/`validatorId` utilisent le motif `!== undefined` afin qu'une chaîne vide
 * envoie `null` — sinon effacer une date planifiée était impossible.
 */
export function toTaskWriteBody(input: TaskInput): Record<string, unknown> {
  return {
    title: input.title,
    ...(input.description !== undefined ? { description: input.description || null } : {}),
    ...(input.clientId ? { clientId: input.clientId } : {}),
    ...(input.projectId ? { projectId: input.projectId } : {}),
    ...(input.teamMemberId ? { teamMemberId: input.teamMemberId } : {}),
    ...(input.assigneeId ? { assigneeId: input.assigneeId } : {}),
    priority: input.priority,
    status: input.status,
    progress: input.progress ?? 0,
    ...(input.startAt !== undefined ? { startAt: input.startAt || null } : {}),
    ...(input.endAt !== undefined ? { endAt: input.endAt || null } : {}),
    ...(input.validatorId !== undefined ? { validatorId: input.validatorId || null } : {}),
    ...(input.tags !== undefined ? { tags: input.tags } : {}),
    ...(input.links !== undefined ? { links: input.links } : {}),
    ...(input.checklist !== undefined ? { checklist: input.checklist } : {}),
    ...(input.files !== undefined && input.files.length > 0 ? { files: input.files } : {}),
  };
}

// ─── Spec §2 — retard ─────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Échéance effective d'une tâche: la fin planifiée (`endAt`), avec repli sur
 * l'ancienne échéance (`dueDate`) pour les tâches créées avant la refonte.
 * Une date sans heure ("YYYY-MM-DD") vaut jusqu'à la fin de la journée locale.
 */
export function getTaskDeadline(task: Task): Date | undefined {
  const raw = task.endAt ?? task.dueDate;
  if (!raw) return undefined;
  const dayOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (dayOnly) {
    return new Date(
      Number(dayOnly[1]),
      Number(dayOnly[2]) - 1,
      Number(dayOnly[3]),
      23,
      59,
      59,
      999,
    );
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/** Seule source de vérité du retard: échéance dépassée et tâche encore ouverte. */
export function isTaskOverdue(task: Task, now: Date = new Date()): boolean {
  if (task.status === "DONE" || task.status === "CANCELLED") return false;
  const deadline = getTaskDeadline(task);
  return !!deadline && deadline.getTime() < now.getTime();
}

/** Jours entiers de retard (minimum 1 dès que la tâche est en retard), 0 sinon. */
export function overdueDays(task: Task, now: Date = new Date()): number {
  const deadline = getTaskDeadline(task);
  if (!deadline || !isTaskOverdue(task, now)) return 0;
  return Math.max(1, Math.floor((now.getTime() - deadline.getTime()) / DAY_MS));
}

/** « En retard » ou « En cours · En retard » selon le statut. */
export function overdueLabel(task: Task): string {
  return task.status === "IN_PROGRESS" ? "En cours · En retard" : "En retard";
}

/** Derives the visual progress value from status+progress, compensating for legacy DB rows with progress=0. */
export function getEffectiveProgress(task: Task): number {
  if (task.status === "DONE") return 100;
  if (task.status === "CANCELLED") return task.progress;
  if (
    (task.status === "IN_PROGRESS" ||
      task.status === "IN_REVIEW" ||
      task.status === "WAITING_CLIENT") &&
    task.progress === 0
  )
    return 25;
  return task.progress;
}

export function getTaskProgressTone(task: Task): "primary" | "success" | "warning" | "danger" {
  if (task.status === "DONE") return "success";
  if (task.status === "WAITING_CLIENT" || task.status === "IN_REVIEW") return "warning";
  if (task.status === "CANCELLED") return "danger";
  return "primary";
}

export function getTaskProgressLabelKey(status: TaskStatus): string {
  if (status === "DONE") return "tasks.progress.completed";
  if (status === "WAITING_CLIENT") return "tasks.progress.waitingClient";
  if (status === "IN_REVIEW") return "tasks.progress.inReview";
  if (status === "CANCELLED") return "tasks.progress.cancelled";
  if (status === "IN_PROGRESS") return "tasks.progress.inProgress";
  return "tasks.progress.notStarted";
}

/** Elapsed chrono since the real start (spec §4.2 "Démarrer"). */
export function formatElapsed(startedAt: string | undefined, now = Date.now()): string | undefined {
  if (!startedAt) return undefined;
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return undefined;
  const minutes = Math.max(0, Math.floor((now - start) / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}j ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}min`;
  return `${mins}min`;
}
